/**
 * Place Details Presenter Component
 * Presentational component for the place details overlay
 */

import React, { useState, useEffect, useRef } from 'react';
import { spacing, strokeColors } from '@/design-system/utils';
import { useOverviewImagesStore } from '@/store/overViewStore';
import { PlaceOverlayHeader } from './place-overlay/PlaceOverlayHeader';
import { PlaceOverlayImageGrid } from './place-overlay/PlaceOverlayImageGrid';
import { PlaceOverlayTabs } from './place-overlay/PlaceOverlayTabs';
// Container components
import { TravelGuideContainer } from './travel-guide/TravelGuideContainer';
import { OverviewContainer } from './overview/OverviewContainer';
import useFetchLocationDetails from '@/hooks/useFetchLocationDetails';
import { ChatLocation } from '@/types/chatTypes';
import { useMediaQuery } from '@/hooks/useMediaQuery';

export interface PlaceDetailsPresenterProps {
  placeName: string;
  location?: ChatLocation;
  onBack?: () => void;
  isOverviewLoading?: boolean;
  isQuickPeekLoading?: boolean;
  sessionId?: string | null;
  userId?: string;
}

export const PlaceDetailsPresenter: React.FC<PlaceDetailsPresenterProps> = ({
  placeName,
  location,
  onBack,
  isOverviewLoading = false,
  isQuickPeekLoading = false,
  sessionId,
  userId,
}) => {
  const [activeTab, setActiveTab] = useState<'guide' | 'overview'>('overview');
  const isMobile = useMediaQuery('(max-width: 768px)');

  const { images: apiImages } = useOverviewImagesStore();
  const { fetchLocationDetails } = useFetchLocationDetails();
  const lastFetchedPlaceRef = useRef<string | null>(null);

  // Reset tab to overview when placeName changes (when a new place is clicked)
  useEffect(() => {
    if (placeName) {
      setActiveTab('overview');
    }
  }, [placeName]);

  // Use image_urls from location data if available, otherwise use API images
  // Handle both image_urls (snake_case) and imageUrls (camelCase) from API
  const locationImageUrls =
    location?.image_urls ||
    (location as unknown as { imageUrls?: string[] })?.imageUrls ||
    [];
  const images =
    locationImageUrls.length > 0
      ? locationImageUrls
      : apiImages.length > 0
        ? apiImages
        : [];

  // Fetch location details when placeName changes (only if we don't have image_urls from location)
  useEffect(() => {
    // Reset ref if placeName is cleared
    if (!placeName) {
      lastFetchedPlaceRef.current = null;
      return;
    }

    // Only fetch if we don't have image_urls from the location data
    // and placeName is different from the last fetched place
    if (
      locationImageUrls.length === 0 &&
      placeName !== lastFetchedPlaceRef.current
    ) {
      lastFetchedPlaceRef.current = placeName;

      // Construct location object for API call
      // The API will handle location lookup with just the name
      const locationForApi = {
        name: placeName,
        state: '', // State is optional, API will try to find location by name
      };
      fetchLocationDetails(locationForApi);
    }
    // Only depend on placeName and locationImageUrls, not fetchLocationDetails to avoid infinite loops
    // fetchLocationDetails is stable enough and the ref prevents duplicate calls
  }, [placeName, locationImageUrls.length, fetchLocationDetails]);

  return (
    <div
      style={{
        width: '100%',
        maxWidth: '100%',
        paddingTop: isMobile ? spacing[4] : spacing[4],
        paddingLeft: isMobile ? spacing[4] : spacing[4],
        paddingRight: isMobile ? spacing[4] : spacing[4],
        paddingBottom: isMobile
          ? `calc(${spacing[4]}px + env(safe-area-inset-bottom, 0px) + ${spacing[20]}px)`
          : spacing[12], // Extra bottom padding to ensure content is visible when scrolling
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? spacing[6] : spacing[4], // Increased gap between sections on mobile for better spacing per Figma design
        boxSizing: 'border-box',
      }}
    >
      {/* Header */}
      <PlaceOverlayHeader
        placeName={placeName}
        rating={
          location?.rating
            ? typeof location.rating === 'number'
              ? location.rating.toFixed(1)
              : String(location.rating)
            : undefined
        }
        onBack={onBack}
      />

      {/* Horizontal Divider */}
      <div
        style={{
          width: '100%',
          height: '1px',
          backgroundColor: strokeColors.base,
        }}
      />

      {/* Images - key by location so switching locations remounts and avoids showing cached/previous images */}
      <PlaceOverlayImageGrid
        key={location?.place_id || placeName || 'no-location'}
        images={images}
        placeName={placeName}
        onViewAll={() => console.log('View all images')}
      />

      {/* Tabs */}
      <PlaceOverlayTabs activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Content - Keep both tabs mounted but hidden (best practice for instant switching) */}
      <div
        style={{
          display: activeTab === 'guide' ? 'block' : 'none',
          width: '100%',
          boxSizing: 'border-box',
          overflow: 'visible', // No scroll on content, scroll is on container
        }}
      >
        <TravelGuideContainer
          placeName={placeName}
          sessionId={sessionId}
          userId={userId}
          isQuickPeekLoading={isQuickPeekLoading}
          location={location}
        />
      </div>

      <div
        style={{
          display: activeTab === 'overview' ? 'block' : 'none',
          width: '100%',
          boxSizing: 'border-box',
          overflow: 'visible', // No scroll on content, scroll is on container
        }}
      >
        <OverviewContainer
          placeName={placeName}
          sessionId={sessionId}
          userId={userId}
          isOverviewLoading={isOverviewLoading}
        />
      </div>
    </div>
  );
};
